import { useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { useCountries } from '@/hooks/useCountries';

interface DialOption {
    value: string;
    country: string;
}

function splitTelephone(telephone: string | undefined | null, dialOptions: DialOption[]): { prefix: string; number: string } {
    if (!telephone) {
        return { prefix: '', number: '' };
    }

    const sorted = [
        ...dialOptions,
    ].sort((a, b) => {
        return b.value.length - a.value.length;
    });

    for (const opt of sorted) {
        if (telephone.startsWith(opt.value)) {
            return { prefix: opt.value, number: telephone.slice(opt.value.length) };
        }
    }

    return { prefix: '', number: telephone };
}

interface Props {
    name: string;
    defaultPrefix?: string;
}

/**
 * Split telephone input with a country dial code prefix dropdown and a
 * local number field. Stores the combined E.164 value (e.g. "+34612345678")
 * in the RHF field.
 */
export function TelephoneInput({ name, defaultPrefix = '+34' }: Props) {
    const { control, setValue } = useFormContext();
    const value = useWatch({ control, name }) as string | undefined | null;
    const { countries } = useCountries();

    const dialOptions: DialOption[] = countries
        .filter((c): c is { dial_code: string; code: string; label: string } => {
            return Boolean(c.dial_code);
        })
        .map((c) => {
            return { value: c.dial_code, country: c.code };
        });

    const [
        prefix,
        setPrefix,
    ] = useState(defaultPrefix);

    // Parse the inbound value once the country dial options are available.
    // Tracked via a "previous dialOptions length" snapshot so we can sync state
    // during render instead of via an effect.
    const [
        parsedFromDialOptionsLength,
        setParsedFromDialOptionsLength,
    ] = useState(0);

    if (dialOptions.length > 0 && parsedFromDialOptionsLength !== dialOptions.length) {
        setParsedFromDialOptionsLength(dialOptions.length);
        setPrefix(splitTelephone(value, dialOptions).prefix || defaultPrefix);
    }

    const number = value?.startsWith(prefix) ? value.slice(prefix.length) : (value ?? '');

    const handlePrefixChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
        const newPrefix = e.target.value;

        setPrefix(newPrefix);
        setValue(name, number ? `${ newPrefix }${ number }` : '', { shouldDirty: true, shouldValidate: true });
    };

    const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newNumber = e.target.value;

        setValue(name, newNumber ? `${ prefix }${ newNumber }` : '', { shouldDirty: true, shouldValidate: true });
    };

    return (
        <div className='flex w-full overflow-hidden rounded-md border border-input bg-background focus-within:ring-1 focus-within:ring-ring focus-within:ring-offset-1 sm:text-sm'>
            <select
                className='shrink-0 border-0 border-r border-input bg-muted px-2 py-2 text-sm text-muted-foreground focus:outline-none'
                onChange={ handlePrefixChange }
                value={ prefix }
            >
                { dialOptions.map((opt) => {
                    return (
                        <option key={ opt.value } value={ opt.value }>{ opt.value }</option>
                    );
                }) }
            </select>
            <input
                className='min-w-0 flex-1 border-0 bg-background px-3 py-2 text-foreground placeholder-muted-foreground focus:outline-none focus:ring-0'
                onChange={ handleNumberChange }
                placeholder='i.e. 612 345 678'
                type='tel'
                value={ number }
            />
        </div>
    );
}
